import {Files, Folders, IContentData, IFile, IFileSystemContent} from "./FileSystemContent";
import {getMimeTypeFromExtension} from "../../../utility/url";

const previewFormats = ["h264_720", "h264_540", "h264_360"];
const customMetadata = [...previewFormats, "image_full", "image_small", "thumb", "uploaded_at"];

interface IFrameAsset {
    asset_type: string
    cover: string
    filesize: number
    h264_720?: string
    id: string
    image_small?: string
    name: string
    original: string
    parent_id: string
    type: string
    updated_at: string
}
interface IFrameAccount {
    display_name: string
    id: string
}
interface IFrameTeam {
    id: string
    name: string
}
export interface IFrameIORead {
    secret: string
}

export class FrameIOContent implements IFileSystemContent {
    private files: Files = {}
    private folders: Folders = {}
    private renders: string = "";
    private rootKey: string = "";
    private delay = 1000;

    constructor(private secret: IFrameIORead, private root: string) {
    }

    getBlockNameKey(blockName: string) {
        const getBlock = (fileName: string, path: string[]) => {
            let root = this.folders[this.renders];

            if (path.every(folderName => {
                const folderKey = root.folders.find(folder => this.folders[folder].name === folderName);

                if (folderKey) {
                    root = this.folders[folderKey];
                    return true;
                }
                return false;
            })) {
                return root.files.find(fileKey => this.files[fileKey].name === `${fileName}.mov`);
            }
        }

        const path = blockName.split("/");

        if (path.length > 1) {
            return getBlock(path.pop()!, path)
        } else if (blockName.match(/^e\d\d_s\d\d\d/) ) {
            return getBlock(blockName, blockName.split("_").slice(0, 2));
        } else {
            return getBlock(blockName, blockName.split("_").slice(0, 1));
        }
    }
    getFileKeys() {
        return Object.keys(this.files);
    }
    getFile(key: string) {
        return this.files[key];
    }
    getFolder(key: string) {
        return this.folders[key];
    }
    getPath(key: string) {
        const path = [];
        const file = this.files[key];
        
        if (file) {
            let parent = file!.parent;

            path.push(key);
            while (parent !== this.rootKey) {
                const folder = this.folders[parent];

                path.unshift(parent);
                parent = folder!.parent;
            }
            path.unshift(this.rootKey);
        }
        return path;
    }
    getPreview(key: string) {
        const previewFormat = previewFormats.find(format => this.files[key].customMetadata[format]);

        return previewFormat ? this.files[key].customMetadata[previewFormat] : "";
    }
    getRoot() {
        return this.rootKey;
    }
    getSoundBankKey() {
        return Object.keys(this.files).find(key => this.files[key].name === "SoundBanks.zip");
    }
    load(): Promise<string | undefined> {
        return this.getFrameworks("accounts").then((accounts: IFrameAccount[]) => {
            const account = accounts.find(account => account.display_name === "Flavourworks");

            if (! account) return undefined;
            return this.getFrameworks(`accounts/${account.id}/teams`).then((teams: IFrameTeam[]) => {
                const team = teams.find(team => team.name === "Flavourworks");

                if (! team) return undefined;
                return this.getFrameworks(`teams/${team.id}/projects`).then(async projects => {
                    const project = projects.find((test: { name: string }) => test.name === this.root);

                    if (! project) return undefined
                    this.rootKey = project.root_asset_id;
                    await this.loadFolders(this.rootKey, "root", ["renders", "soundbanks", "textures"]);
                    this.renders = Object.keys(this.folders)
                        .find(folder => this.folders[folder].name === "renders" &&
                            this.folders[folder].parent === project.root_asset_id) || "";
                    return this.renders !== "" ? project.root_asset_id : undefined;
                });
            });
        });
    }
    read(data: IContentData) {
        this.files = data.files;
        this.folders = data.folders;
        this.rootKey = Object.keys(data.folders).find(key =>
            data.folders[key].name === "root" && data.folders[key].parent === "") || "";
        this.renders = Object.keys(data.folders).find(key =>
            data.folders[key].name === "renders" && data.folders[key].parent === this.rootKey) || "";
    }
    write() {
        return {
            files: this.files,
            folders: this.folders
        };
    }

    private getCustomMetadata(asset: IFrameAsset) {
        return Object.fromEntries(Object.entries(asset).filter(([key, value]) => value && customMetadata.includes(key)));
    }

    private loadVersionStack(assetId: string, parentId: string): Promise<{id: string, file:IFile}> {
        return this.getFrameworks(`assets/${assetId}/children`).then((assets: IFrameAsset[]) => {
            const latest = assets[0];

            return {
                id: latest.id,
                file: {
                    contentType: getMimeTypeFromExtension(latest.original),
                    customMetadata: this.getCustomMetadata(latest),
                    name: latest.name,
                    parent: parentId,
                    size: latest.filesize,
                    source: latest.original,
                    timestamp: Date.parse(latest.updated_at),
                }
            };
        });
    }
    private sleep(delay: number) {
        return new Promise<void>((resolve) => {
            setTimeout(() => resolve(), delay);
        });
    }

    private async loadFolders(root: string, name: string, rootFilter: string[]) {
        const folders = [root];

        this.folders[root] = {
            name,
            parent: "",
            files: [],
            folders: []
        };
        while (folders.length) {
            const folderId = folders.pop() as string;
            const assets = await this.getFrameworks(`assets/${folderId}/children`);

            if (assets.errors) {
                console.error(assets.message);
            } else {
                for (let asset of assets) {
                    if (asset.type === "file") {
                        this.folders[folderId].files.push(asset.id);
                        this.files[asset.id] = {
                            contentType: getMimeTypeFromExtension(asset.original),
                            customMetadata: this.getCustomMetadata(asset),
                            name: asset.name,
                            parent: asset.parent_id,
                            size: asset.filesize,
                            source: asset.original,
                            timestamp: Date.parse(asset.updated_at),
                        }
                    } else if (asset.type === "folder") {
                        if (folderId !== root || rootFilter.includes(asset.name)) {
                            this.folders[folderId].folders.push(asset.id);
                            this.folders[asset.id] = {
                                name: asset.name,
                                parent: folderId,
                                files: [],
                                folders: []
                            };
                            folders.push(asset.id);
                        }
                    } else if (asset.type === "version_stack") {
                        const {id, file} = await this.loadVersionStack(asset.id, folderId);

                        this.folders[folderId].files.push(id);
                        this.files[id] = file;
                    }
                }
            }
        }
    }
    private getFrameworks(request: string): Promise<any> {
        return fetch(`https://api.frame.io/v2/${request}`, {
            headers: {
                Authorization: `Bearer ${this.secret.secret}`
            },
            method: "GET",
        }).then(response => response.json()).then(response => {
            if (response.code === 429) {
                // Rate limited so wait a moment and try again
                return this.sleep(this.delay).then(() => {
                    this.delay *= 2;
                    return this.getFrameworks(request);
                });
            }
            return response;
        });
    }
}